/**
 * useNavigation Hook
 *
 * Central hook for navigation state management.
 * Handles URL sync, role validation, last-view memory, and telemetry.
 *
 * @see docs/architecture/2026-01-08/NAVIGATION-RESTRUCTURE-MASTER-PLAN.md
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  parseHash,
  resolveLegacyAlias,
  navigateToHash,
  getCurrentRoute,
  getLastViewsBySection,
  saveLastViewsBySection,
  emitNavEvent,
} from '../utils/navigationUtils';
import {
  SECTIONS,
  getDefaultSection,
  getDefaultView,
  validateAndCorrectNavState,
  isSectionVisible,
  isViewVisible,
} from '../config/navigationConfig';
import { getCurrentViewTitle } from '../config/viewRegistry';

/**
 * Sanitize lastViews when user role changes
 * Removes views that user can no longer access
 * @param {Record<string, string>} lastViews - Stored last views
 * @param {object} user - User object
 * @returns {Record<string, string>} Sanitized last views
 */
function sanitizeLastViews(lastViews, user) {
  const sanitized = {};
  for (const [sectionId, viewId] of Object.entries(lastViews)) {
    const section = SECTIONS[sectionId];
    if (!section || !isSectionVisible(section, user)) {
      continue; // Skip sections user can't access
    }
    const viewConfig = section.views.find((v) => v.id === viewId);
    if (viewConfig && isViewVisible(viewConfig, user)) {
      sanitized[sectionId] = viewId;
    }
  }
  return sanitized;
}

/**
 * Navigation hook
 * @param {object} user - User object with isAdmin and isPM flags
 * @returns {object} Navigation state and methods
 */
export function useNavigation(user) {
  const [activeSection, setActiveSection] = useState(null);
  const [activeView, setActiveView] = useState(null);
  const [activeParam, setActiveParam] = useState(null); // For views that need params (e.g., quoteNumber)
  const [isNavReady, setIsNavReady] = useState(false);
  const [lastViews, setLastViews] = useState({});

  // Track previous user role for sanitization
  const prevUserRef = useRef(null);

  // =============================================================================
  // Initialize from URL on mount
  // =============================================================================

  useEffect(() => {
    // Sanitize lastViews if user role changed
    if (prevUserRef.current !== null) {
      const prevIsAdmin = prevUserRef.current?.isAdmin;
      const prevIsPM = prevUserRef.current?.isPM;
      if (prevIsAdmin !== user?.isAdmin || prevIsPM !== user?.isPM) {
        const stored = getLastViewsBySection();
        const sanitized = sanitizeLastViews(stored, user);
        saveLastViewsBySection(sanitized);
        setLastViews(sanitized);
      }
    }
    prevUserRef.current = user;

    // Load lastViews from sessionStorage
    const storedLastViews = getLastViewsBySection();
    const sanitizedLastViews = sanitizeLastViews(storedLastViews, user);
    setLastViews(sanitizedLastViews);

    // Get current route from URL
    const route = getCurrentRoute();
    let section = route?.section;
    let view = route?.view;
    let param = route?.param; // e.g., quoteNumber for project-page

    // If no route, use defaults
    if (!section) {
      section = getDefaultSection(user);
      view = getDefaultView(section, user);
    }

    // If section but no view, check lastViews or use default
    if (section && !view) {
      view = sanitizedLastViews[section] || getDefaultView(section, user);
    }

    // Validate and correct
    const validated = validateAndCorrectNavState(section, view, user);

    // Update URL if corrected (will trigger hashchange, but we set state immediately)
    if (validated.corrected) {
      navigateToHash(validated.section, validated.view, param);
      emitNavEvent('nav_corrected', {
        section: validated.section,
        view: validated.view,
        param,
        reason: validated.reason,
        original_section: section,
        original_view: view,
      }, user);
    } else {
      // Update URL to canonical format if it was a legacy alias
      const hash = window.location.hash;
      if (resolveLegacyAlias(hash)) {
        navigateToHash(validated.section, validated.view, param);
        emitNavEvent('legacy_redirect', {
          section: validated.section,
          view: validated.view,
          param,
          legacy_hash: hash,
        }, user);
      }
    }

    // Set initial state
    setActiveSection(validated.section);
    setActiveView(validated.view);
    setActiveParam(param || null);
    setIsNavReady(true);

    // Emit initial navigation event
    emitNavEvent('nav_init', {
      section: validated.section,
      view: validated.view,
      param,
    }, user);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // =============================================================================
  // Handle hashchange events
  // =============================================================================

  useEffect(() => {
    function handleHashChange() {
      const route = getCurrentRoute();
      let section = route?.section;
      let view = route?.view;
      let param = route?.param; // e.g., quoteNumber for project-page

      // If no route, use defaults
      if (!section) {
        section = getDefaultSection(user);
        view = getDefaultView(section, user);
      }

      // If section but no view, check lastViews or use default
      if (section && !view) {
        view = lastViews[section] || getDefaultView(section, user);
      }

      // Validate and correct
      const validated = validateAndCorrectNavState(section, view, user);

      // If corrected, update URL (will trigger another hashchange)
      if (validated.corrected) {
        navigateToHash(validated.section, validated.view, param);
        emitNavEvent('nav_corrected', {
          section: validated.section,
          view: validated.view,
          param,
          reason: validated.reason,
        }, user);
        return; // Don't update state yet, wait for corrected hashchange
      }

      // Update state
      setActiveSection(validated.section);
      setActiveView(validated.view);
      setActiveParam(param || null);

      // Update lastViews
      setLastViews((prev) => {
        const updated = { ...prev, [validated.section]: validated.view };
        saveLastViewsBySection(updated);
        return updated;
      });

      // Emit navigation event
      emitNavEvent('nav_change', {
        section: validated.section,
        view: validated.view,
        param,
      }, user);
    }

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [user, lastViews]);

  // =============================================================================
  // Navigation Methods
  // =============================================================================

  /**
   * Navigate to a section/view with optional param
   * @param {string} section - Section ID
   * @param {string} view - View ID (optional, uses last view or default)
   * @param {string} [param] - Optional param (e.g., quoteNumber for project-page)
   */
  const navigateTo = useCallback(
    (section, view, param) => {
      // If no view specified, use last view or default
      if (!view) {
        view = lastViews[section] || getDefaultView(section, user);
      }

      // Validate
      const validated = validateAndCorrectNavState(section, view, user);

      // Navigate (triggers hashchange which updates state)
      navigateToHash(validated.section, validated.view, param);
    },
    [user, lastViews]
  );

  /**
   * Check if user can access a section
   * @param {string} sectionId - Section ID
   * @returns {boolean} Whether user can access
   */
  const canAccessSection = useCallback(
    (sectionId) => {
      const section = SECTIONS[sectionId];
      return section ? isSectionVisible(section, user) : false;
    },
    [user]
  );

  /**
   * Check if user can access a view
   * @param {string} sectionId - Section ID
   * @param {string} viewId - View ID
   * @returns {boolean} Whether user can access
   */
  const canAccessView = useCallback(
    (sectionId, viewId) => {
      const section = SECTIONS[sectionId];
      if (!section || !isSectionVisible(section, user)) return false;
      const viewConfig = section.views.find((v) => v.id === viewId);
      return viewConfig ? isViewVisible(viewConfig, user) : false;
    },
    [user]
  );

  // =============================================================================
  // Derived State
  // =============================================================================

  /**
   * Get current view title
   */
  const currentTitle = useMemo(() => {
    return getCurrentViewTitle(activeView);
  }, [activeView]);

  /**
   * Get current section config
   */
  const currentSection = useMemo(() => {
    return activeSection ? SECTIONS[activeSection] : null;
  }, [activeSection]);

  // =============================================================================
  // Return
  // =============================================================================

  return {
    // State
    activeSection,
    activeView,
    activeParam, // e.g., quoteNumber for project-page
    isNavReady,
    lastViews,

    // Derived
    currentTitle,
    currentSection,

    // Methods
    navigateTo,
    canAccessSection,
    canAccessView,
  };
}

export default useNavigation;
